import React, { useEffect, useState } from "react";
import { centered, useTextStyles } from "../styles";

import QuickActions from "../components/QuickActions";
import QuickLinks from "../components/QuickLinks";
import FwDropdown from "../components/FwDropdown";
import { getPipelines } from "../../utils/apiclient";
import { attemptTokenRefresh, clearStorage } from "../../utils/auth";
import CustomNotification from "../components/CustomNotification";
import Recalculate from "../components/Recalculate";
import { Screen } from "../../utils/common/constants";
import { visibilityProperties } from "@fundwave/ui-utils/src/visibility-properties";

interface HomeScreenProps {
  navigate: Function;
}

const Home: React.FC<HomeScreenProps> = ({ navigate }) => {
  const textStyles = useTextStyles();

  const username = localStorage.getItem("username");
  const domains: Array<string> = JSON.parse(localStorage.getItem("domains") || "[]");

  const extractTeamNameRegex = /\/\/([^.]+)/;
  const domainOptions = domains.map((domain) => ({ value: domain, label: domain.match(extractTeamNameRegex)?.[1] || domain }));

  const [pipelines, setPipelines] = useState<{ label: string; value: string; group: string; groupId: string }[]>([]);
  const [isLoadingPipelines, setIsLoadingPipelines] = useState(true);
  const [team, setTeam] = useState(localStorage.getItem("team") || domains?.[0]);

  useEffect(() => {
    fetchPipelines();
  }, []);

  useEffect(() => {

    const { pageHiddenProperty, visibilityChangeEventName } = visibilityProperties();

    
    const handleVisibilityChange = async () => {
      
      const isVisible = !document[pageHiddenProperty];
      
      if (isVisible) {
        const isTokenValid = await attemptTokenRefresh("window_focus");
        if (!isTokenValid) {
          clearStorage("refresh_failed_on_focus");
          navigate(Screen.Login);
        }
      }
      
    };
 
    document.addEventListener(visibilityChangeEventName, handleVisibilityChange);

    return () => {
      document.removeEventListener(visibilityChangeEventName, handleVisibilityChange);
    };
  }, [navigate]);

  function getSubHeaderText() {
    const subHeaders: string[] = [];

    if (domains.length === 1) {
      subHeaders.push(team.match(extractTeamNameRegex)?.[1] || team);
    }

    if (pipelines.length === 1 && pipelines?.[0]?.label) {
      subHeaders.push(pipelines[0].label);
    }

    return subHeaders.join(" | ");
  }

  return (
    <>
      <div style={{ ...centered, flexDirection: "column", gap: "8px", textAlign: "center" }}>
        <span className={textStyles.heading1}>Welcome to Fundwave, {username}</span>
        {(domains.length === 1 || pipelines.length === 1) && <span className={textStyles.normalText}>{getSubHeaderText()}</span>}
      </div>

      {localStorage.getItem("client") === "gsheets" && (
        <>
          <CustomNotification label="Important: Others with access to this spreadsheet will be able to use Fundwave Functions with the same permissions as you." />
          <Recalculate />
        </>
      )}

      {localStorage.getItem("client") === "excel" && <Recalculate />}

      {domains.length > 1 && (
        <FwDropdown
          id="team-dropdown"
          options={domainOptions}
          label="Select a team"
          defaultSelectedOptions={[team]}
          defaultValue={team.match(extractTeamNameRegex)?.[1]}
          onOptionSelect={(_, data) => handleTeamSelection(data.optionValue)}
        />
      )}
      {pipelines.length !== 1 && (
        <FwDropdown
          id="pipeline-dropdown"
          label="Select a Dealflow Pipeline"
          options={pipelines}
          groupBy={true}
          isLoading={isLoadingPipelines}
          defaultValue={pipelines[0]?.label}
          defaultSelectedOptions={[pipelines[0]?.value]}
          emptyLabel="You're not a part of any dealflow pipeline"
          onOptionSelect={(_, data) => handlePipelineSelection(data.optionValue)}
        />
      )}

      {localStorage.getItem("client") === "excel" && <QuickActions navigate={navigate} />}
      <QuickLinks />
    </>
  );

  function handleTeamSelection(value?: string) {
    if (!value) return;

    localStorage.setItem("team", value);
    setTeam(value);
  }

  function handlePipelineSelection(value?: string) {
    if (!value) return;

    const domainId = pipelines.find((pipeline) => pipeline.value === value)?.groupId || "";

    localStorage.setItem("pipelineId", value);
    localStorage.setItem("dealflowDomainId", domainId);
  }

  async function fetchPipelines() {
    setIsLoadingPipelines(true);
    try {
      const pipelines = await getPipelines();

      setPipelines(pipelines);
    } catch (error) {
      console.error(`Error while fetching pipelines. ${error}`);
    } finally {
      setIsLoadingPipelines(false);
    }
  }
};

export default Home;
